import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { StateService, Transition } from '@uirouter/core';

import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import {
  PRESENTATION_ACCESS,
  SCHEDULE_ACCESS,
} from 'src/app/auth/types/access-level';
import { EditorService } from 'src/app/editor/services/editor.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { CompanyAssetsService } from 'src/app/shared/services/company-assets.service';
import { DisplayService } from '../../services/display.service';

@Component({
  selector: 'activation-notice',
  templateUrl: './activation-notice.component.html',
  styleUrls: ['./activation-notice.component.scss'],
  standalone: false,
})
export class ActivationNoticeComponent implements OnInit {
  isFirstDisplay: boolean;
  hasPresentations: boolean;

  private readonly destroyRef = inject(DestroyRef);
  private readonly companyAssetsService = inject(CompanyAssetsService);
  private readonly broadcaster = inject(BroadcasterService);
  private hasPresentationsRequestId = 0;

  get showActivationNotice() {
    // Check that things are loaded & that display is activated
    if (
      this.isFirstDisplay === undefined ||
      this.hasPresentations === undefined ||
      !this.displayService.display?.lastActivityDate
    ) {
      return false;
    }

    // Show notice if the user has just completed the activation flow
    if (this.transition.params().activated) {
      return true;
    }

    // Show notice if content needs to be set up
    return this.showAddScheduleNotice || this.showAddPresentationNotice;
  }

  get showAddScheduleNotice() {
    return (
      !this.displayService.hasSchedule(this.displayService.display) &&
      this.hasPresentations &&
      this.permissionService.hasAccess(SCHEDULE_ACCESS.FULL_ACCESS)
    );
  }

  get showAddPresentationNotice() {
    return (
      !this.hasPresentations &&
      this.permissionService.hasAccess(PRESENTATION_ACCESS.EDITOR)
    );
  }

  constructor(
    private transition: Transition,
    private permissionService: PermissionService,
    private displayService: DisplayService,
    public editorService: EditorService,
    private displayApiService: DisplayApiService,
    private stateService: StateService,
  ) {
    const companyAssetsUpdated = this.broadcaster.on(
      'companyAssetsUpdated',
      () => {
        this.loadHasPresentations();
      },
    );
    const selectedCompanyChanged = this.broadcaster.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this.loadHasPresentations();
      },
    );

    this.destroyRef.onDestroy(() => {
      companyAssetsUpdated.unsubscribe();
      selectedCompanyChanged.unsubscribe();
    });
  }

  ngOnInit(): void {
    this.displayApiService.summary().then((summary) => {
      this.isFirstDisplay = summary.online + summary.offline <= 1;
    });

    this.loadHasPresentations();
  }

  navigateToCreatePresentation(): void {
    this.stateService.go('apps.editor.create');
  }

  private loadHasPresentations(): void {
    const requestId = ++this.hasPresentationsRequestId;
    const previousHasPresentations = this.hasPresentations;
    this.hasPresentations = undefined;

    this.companyAssetsService.hasPresentations().then(
      (result) => {
        if (requestId !== this.hasPresentationsRequestId) {
          return;
        }

        this.hasPresentations = result;
      },
      () => {
        if (requestId !== this.hasPresentationsRequestId) {
          return;
        }

        this.hasPresentations = previousHasPresentations;
      },
    );
  }
}
